import { getClientReferenceId } from "./amplitude";
import { paymentConfig } from "../config/paymentConfig";
import { getPaywallContext } from "./paywallContext";
import { getUtmMetadataFromSearch } from "./utm";
import { getEmailAttributionMetadata } from "./checkoutMetadata";

export const PAYWALL_EVENT_ENDPOINT = paymentConfig.quizPaywallEventUrl;

const oneTimeEvents = new Set([
  "paywall_seen",
  "payment_options_seen",
  "wallet_available",
  "wallet_unavailable",
  "card_form_opened",
  "upsell_seen",
]);

const allowedPayloadKeys = new Set([
  "wallet_attempt_id", "wallet_surface", "authorization_received",
  "recovery_id", "event_id", "occurred_at", "elapsed_ms", "can_check_status",
  "product_key", "flow_version", "funnel", "step", "chain_id", "transaction_id",
  "amount",
  "apple_pay",
  "currency",
  "error_code",
  "error_stage",
  "google_pay",
  "link",
  "offer_id",
  "payment_request_supported",
  "price_id",
  "reason",
  "status",
  "wallet_type",
  "wallet_attempt_id",
  "wallet_surface",
  "authorization_received",
]);

const sensitivePayloadKeyPattern =
  /email|card|cvc|cvv|client_secret|secret|photo|image|img|url/i;

const rootUtmKeys = new Map([
  ["utm_source", 64],
  ["utm_medium", 64],
  ["utm_campaign", 128],
  ["utm_content", 128],
  ["utm_term", 128],
]);

export const resolvePaywall = (pathname = getWindowPathname()) => {
  const routePaywall = getPaywallContext(pathname).paywall;
  if (routePaywall) return routePaywall;

  try {
    return sessionStorage.getItem("lastPaywall") || "";
  } catch (_) {
    return "";
  }
};

export const getPaywallEventPayload = ({
  eventName,
  clientReferenceId,
  paywall,
  route,
  paymentPath,
  payload,
} = {}) => {
  const resolvedClientReferenceId = String(
    clientReferenceId || getClientReferenceId() || "",
  ).trim();
  const resolvedRoute = route || getWindowPathname();
  const resolvedPaywall = String(paywall || resolvePaywall(resolvedRoute) || "").trim();

  if (!eventName || !resolvedClientReferenceId) {
    return null;
  }

  return {
    client_reference_id: resolvedClientReferenceId,
    event_name: eventName,
    paywall: resolvedPaywall,
    route: resolvedRoute,
    ...getRootUtmPayload(),
    ...getEmailAttributionMetadata(getWindowSearch()),
    ...(paymentPath ? { payment_path: paymentPath } : {}),
    payload: sanitizeAnalyticsPayload(payload),
  };
};

export const sendPaywallEvent = (eventName, options = {}) => {
  if (typeof window === "undefined") return false;

  const eventPayload = getPaywallEventPayload({
    ...options,
    eventName,
  });

  if (!eventPayload) return false;
  persistTrackingContext(eventPayload);
  return sendPreparedPaywallEvent(eventPayload);
};

// Keep Stripe's user-activation/click stack free of analytics transport.
// Freeze attribution now; delivery remains best-effort like sendPaywallEvent.
export const sendPaywallEventDeferred = (eventName, options = {}) => {
  try {
    const eventPayload = getPaywallEventPayload({ ...options, eventName });
    if (!eventPayload) return;
    persistTrackingContext(eventPayload);
    setTimeout(() => {
      try { sendPreparedPaywallEvent(eventPayload); } catch (_) {}
    }, 0);
  } catch (_) {}
};

function sendPreparedPaywallEvent(eventPayload) {
  if (oneTimeEvents.has(eventPayload.event_name) && !markPaywallEventIfNew(eventPayload)) {
    return false;
  }

  const body = JSON.stringify(eventPayload);

  try {
    if (navigator?.sendBeacon) {
      const sent = navigator.sendBeacon(
        PAYWALL_EVENT_ENDPOINT,
        new Blob([body], { type: "application/json" }),
      );
      if (sent) return true;
    }
  } catch (_) {}

  try {
    fetch(PAYWALL_EVENT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
    return true;
  } catch (_) {
    return false;
  }
}

export const trackCardOptionsClick = () =>
  sendPaywallEvent("card_options_clicked", { paymentPath: "card" });

export const getWalletAvailabilityPayload = (availability) => ({
  payment_request_supported: Boolean(availability),
  ...(availability?.applePay ? { apple_pay: true, wallet_type: "apple_pay" } : {}),
  ...(availability?.googlePay ? { google_pay: true, wallet_type: "google_pay" } : {}),
  ...(availability?.link ? { link: true, wallet_type: "link" } : {}),
});

export function sanitizeAnalyticsPayload(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((safe, [key, entry]) => {
    if (
      Object.keys(safe).length < 24 &&
      allowedPayloadKeys.has(key) &&
      !sensitivePayloadKeyPattern.test(key) &&
      isSafePayloadValue(entry)
    ) {
      safe[key] = normalizePayloadValue(entry);
    }
    return safe;
  }, {});
}

function isSafePayloadValue(value) {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function normalizePayloadValue(value) {
  if (typeof value === "string") return value.trim().slice(0, 256);
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  return value;
}

function getRootUtmPayload(search = getWindowSearch()) {
  const urlUtm = getUtmMetadataFromSearch(search, {
    maxKeys: 50,
    maxKeyLength: 40,
    maxValueLength: 128,
  });

  return Object.entries(urlUtm).reduce((safe, [rawKey, rawValue]) => {
    const key = String(rawKey || "").toLowerCase();
    const maxLength = rootUtmKeys.get(key);
    const value = String(rawValue || "").trim();

    if (maxLength && value) {
      safe[key] = value.slice(0, maxLength);
    }

    return safe;
  }, {});
}

function markPaywallEventIfNew(eventPayload) {
  try {
    const storageKey = [
      "paywall_event",
      eventPayload.event_name,
      eventPayload.client_reference_id,
      eventPayload.paywall,
      eventPayload.route,
      ...(eventPayload.email_message_ref ? [eventPayload.email_message_ref] : []),
      ...(eventPayload.payload.flow_version === 2
        ? [eventPayload.payload.chain_id, eventPayload.payload.step] : []),
    ].join("|");
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, "1");
    return true;
  } catch (_) {
    return true;
  }
}

function persistTrackingContext(eventPayload) {
  try {
    if (eventPayload.paywall) {
      sessionStorage.setItem("lastPaywall", eventPayload.paywall);
    }
    if (eventPayload.client_reference_id) {
      sessionStorage.setItem(
        "clientReferenceId",
        eventPayload.client_reference_id,
      );
    }
  } catch (_) {}
}

function getWindowPathname() {
  return typeof window !== "undefined" ? window.location?.pathname || "" : "";
}

function getWindowSearch() {
  return typeof window !== "undefined" ? window.location?.search || "" : "";
}
